import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface SubscriptionStatusProps {
  subscription: any;
  onUpdate: () => void;
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({ subscription, onUpdate }) => {
  const { t } = useTranslation();
  const [unsubscribeDialogOpen, setUnsubscribeDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleUnsubscribe = async () => {
    try {
      setLoading(true);
      await api.post(`/reminders/unsubscribe/${subscription.email}`);
      setUnsubscribeDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error unsubscribing:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!subscription) return null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            {t('reminders.subscription.title')}
          </Typography>
          <Box>
            {subscription.is_active ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={t('reminders.subscription.active')}
                color="success"
                variant="filled"
              />
            ) : (
              <Chip
                icon={<CancelIcon />}
                label={t('reminders.subscription.inactive')}
                color="error"
                variant="filled"
              />
            )}
            {subscription.service_tier === 'premium' ? (
              <Chip
                icon={<StarIcon />}
                label={t('reminders.subscription.premium')}
                color="primary"
                variant="filled"
                sx={{ ml: 1 }}
              />
            ) : (
              <Chip
                label={t('reminders.subscription.free')}
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 13px)' } }}>
            <Box display="flex" alignItems="center">
              <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('reminders.subscription.email')}
                </Typography>
                <Typography variant="body2">
                  {subscription.email}
                </Typography>
              </Box>
            </Box>
          </Box>

          {subscription.pilot_name && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 13px)' } }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('reminders.subscription.name')}
                </Typography>
                <Typography variant="body2">
                  {subscription.pilot_name}
                </Typography>
              </Box>
            </Box>
          )}

          {subscription.phone && (
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 13px)' } }}>
              <Box display="flex" alignItems="center">
                <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('reminders.subscription.phone')}
                  </Typography>
                  <Typography variant="body2">
                    {subscription.phone}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(33.333% - 13px)' } }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('common.createdAt')}
              </Typography>
              <Typography variant="body2">
                {new Date(subscription.subscription_date).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setUnsubscribeDialogOpen(true)}
          >
            {t('reminders.subscription.unsubscribe')}
          </Button>
        </Box>

        {/* Unsubscribe Confirmation Dialog */}
        <Dialog open={unsubscribeDialogOpen} onClose={() => setUnsubscribeDialogOpen(false)}>
          <DialogTitle>{t('reminders.subscription.unsubscribe')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('reminders.subscription.confirmUnsubscribe')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUnsubscribeDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUnsubscribe}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {t('reminders.subscription.unsubscribe')}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default SubscriptionStatus;